import { Alert, Button, Card, CardContent, Dialog, DialogContent, DialogTitle, Grid, LinearProgress, List, ListItem, ListItemText, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { PlanPicker } from "../billing_plan_catalog/PlanPicker";
import { ErrorState, LoadingState, PageHeader, PermissionGate, StatusChip } from "../components";
import { formatDate, formatDateTime, formatMoney } from "../i18n/format";
import help from "./HELP.md?raw";
import { useBillingEmail, useCancel, useChange, useCheckout, useHistory, useInvoices, usePlans, usePortal, useReactivate, useSubscription } from "./useData";

function Usage({ label, used, limit }: { label: string; used: number; limit?: number }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" justifyContent="space-between"><Typography variant="body2">{label}</Typography><Typography variant="body2">{used}{limit ? ` / ${limit}` : ""}</Typography></Stack>
      {limit ? <LinearProgress variant="determinate" value={pct} color={pct >= 90 ? "warning" : "primary"} /> : null}
    </Stack>
  );
}

export function Billing() {
  const { hasPermission } = useAuth();
  const [params] = useSearchParams();
  const sub = useSubscription();
  const plans = usePlans();
  const invoices = useInvoices();
  const history = useHistory();
  const checkout = useCheckout();
  const portal = usePortal();
  const change = useChange();
  const cancel = useCancel();
  const reactivate = useReactivate();
  const email = useBillingEmail();
  const [choosing, setChoosing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const [seats, setSeats] = useState<number | null>(null);
  const [billingEmail, setBillingEmail] = useState<string | null>(null);
  const canChange = hasPermission("billing.subscription.change");

  if (sub.isLoading || plans.isLoading) return <LoadingState />;
  if (sub.isError) return <ErrorState message={sub.error.message} onRetry={() => void sub.refetch()} />;
  const s = sub.data!;
  const trialOrExpired = ["trialing", "expired", "canceled"].includes(s.status);
  const anyError = [checkout, portal, change, cancel, reactivate, email].find((m) => m.isError)?.error?.message;
  const goPortal = () => portal.mutate(undefined, { onSuccess: (d) => window.location.assign(d.url) });

  return (
    <>
      <PageHeader help={help} title="Billing" subtitle="Plan, seats, payment method and invoices" crumbs={[{ label: "Settings" }, { label: "Billing" }]}
        actions={<StatusChip status={s.status} />} />
      {params.get("checkout") === "success" && <Alert severity="success" sx={{ mb: 2 }}>Thanks! Your subscription is being activated; this page refreshes automatically.</Alert>}
      {params.get("checkout") === "cancelled" && <Alert severity="info" sx={{ mb: 2 }}>Checkout cancelled. Your trial continues.</Alert>}
      {anyError && <Alert severity="error" sx={{ mb: 2 }}>{anyError}</Alert>}
      {s.status === "trialing" && <Alert severity="info" sx={{ mb: 2 }} action={canChange && <Button color="inherit" size="small" onClick={() => setChoosing(true)}>Choose a plan</Button>}>Your free trial ends in {s.trial_days_left} day{s.trial_days_left === 1 ? "" : "s"} ({formatDate(s.trial_ends_at)}).</Alert>}
      {s.status === "expired" && <Alert severity="warning" sx={{ mb: 2 }} action={canChange && <Button color="inherit" size="small" onClick={() => setChoosing(true)}>Choose a plan</Button>}>Your trial has ended. The workspace is read-only{s.grace_until ? ` until ${formatDate(s.grace_until)}` : ""}; choose a plan to keep working.</Alert>}
      {s.status === "past_due" && <Alert severity="error" sx={{ mb: 2 }} action={canChange && <Button color="inherit" size="small" onClick={goPortal}>Update payment method</Button>}>Your last payment failed. Update your card{s.grace_until ? ` before ${formatDate(s.grace_until)}` : ""} to avoid read-only mode.</Alert>}
      {s.status === "unpaid" && <Alert severity="error" sx={{ mb: 2 }} action={canChange && <Button color="inherit" size="small" onClick={goPortal}>Update payment method</Button>}>Your subscription is unpaid and the workspace is read-only.</Alert>}
      {s.cancel_at_period_end && <Alert severity="warning" sx={{ mb: 2 }} action={canChange && <Button color="inherit" size="small" onClick={() => reactivate.mutate()}>Keep my subscription</Button>}>Your subscription ends on {formatDate(s.current_period_end)}.</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <div>
                <Typography variant="h5">{s.plan.name}{s.addons.length ? ` + ${s.addons.map((a) => a.name).join(", ")}` : ""}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatMoney(s.interval === "year" ? Number(s.plan.annual_price) / 12 : s.plan.monthly_price, s.plan.currency, 0)}/month, billed {s.interval === "year" ? "annually" : "monthly"} · {s.seats} seats
                  {s.current_period_end ? ` · renews ${formatDate(s.current_period_end)}` : ""}
                </Typography>
              </div>
              {canChange && <Button variant="outlined" onClick={() => setChoosing(true)}>{trialOrExpired ? "Choose plan" : "Change plan"}</Button>}
            </Stack>
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Usage label="Members" used={s.usage.users} limit={s.limits.users} />
              <Usage label="Sites" used={s.usage.sites} limit={s.limits.sites} />
              <Usage label="API requests today" used={s.usage.api_requests_today} limit={s.limits.api_requests_per_day} />
            </Stack>
            {canChange && !trialOrExpired && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <TextField size="small" type="number" label="Seats" value={seats ?? s.seats} onChange={(e) => setSeats(Number(e.target.value))} sx={{ width: 120 }} inputProps={{ min: s.plan.included_users }} />
                <Button disabled={seats === null || seats === s.seats || change.isPending} onClick={() => change.mutate({ seats: seats! }, { onSuccess: () => setSeats(null) })}>Update seats</Button>
                <Typography variant="caption" color="text.secondary">Prorated automatically.</Typography>
              </Stack>
            )}
          </CardContent></Card>
          <Card sx={{ mt: 2 }}><CardContent>
            <Typography variant="subtitle2" gutterBottom>Invoices</Typography>
            <List dense>
              {invoices.data?.length === 0 && <ListItem><ListItemText secondary="No invoices yet." /></ListItem>}
              {invoices.data?.map((inv) => (
                <ListItem key={inv.id} secondaryAction={<Stack direction="row" spacing={1}><Button size="small" href={inv.hosted_invoice_url} target="_blank">View</Button><Button size="small" href={inv.invoice_pdf} target="_blank">PDF</Button></Stack>}>
                  <ListItemText primary={`${inv.number} · ${formatMoney(inv.total / 100, inv.currency.toUpperCase())}`} secondary={`${formatDate(new Date(inv.created * 1000))} · ${inv.status}`} />
                </ListItem>
              ))}
            </List>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card><CardContent>
            <Typography variant="subtitle2" gutterBottom>Payment method</Typography>
            <Typography variant="body2">{s.payment_method?.last4 ? `${s.payment_method.brand?.toUpperCase()} •••• ${s.payment_method.last4} (exp ${s.payment_method.exp})` : "No payment method on file."}</Typography>
            {canChange && <Button sx={{ mt: 1 }} onClick={goPortal} disabled={portal.isPending}>Manage payment & billing details</Button>}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
              <TextField size="small" label="Billing email" value={billingEmail ?? s.billing_email} onChange={(e) => setBillingEmail(e.target.value)} disabled={!canChange} />
              {canChange && <Button disabled={billingEmail === null || email.isPending} onClick={() => email.mutate(billingEmail!, { onSuccess: () => setBillingEmail(null) })}>Save</Button>}
            </Stack>
          </CardContent></Card>
          <Card sx={{ mt: 2 }}><CardContent>
            <Typography variant="subtitle2" gutterBottom>History</Typography>
            <List dense>
              {history.data?.slice(0, 8).map((h, i) => <ListItem key={i}><ListItemText primary={h.kind.replace(/_/g, " ")} secondary={formatDateTime(h.created_at)} /></ListItem>)}
            </List>
          </CardContent></Card>
          <PermissionGate permission="billing.subscription.cancel">
            {!s.cancel_at_period_end && s.status !== "canceled" && (
              <Card sx={{ mt: 2, borderColor: "error.main" }}><CardContent>
                <Typography variant="subtitle2" gutterBottom>Cancel subscription</Typography>
                <Typography variant="body2" color="text.secondary">Access continues until the end of the billing period; data stays readable for 7 days after that.</Typography>
                <Button color="error" sx={{ mt: 1 }} onClick={() => setCancelling(true)}>Cancel…</Button>
              </CardContent></Card>
            )}
          </PermissionGate>
        </Grid>
      </Grid>

      <Dialog open={choosing} onClose={() => setChoosing(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{trialOrExpired ? "Choose your plan" : "Change plan"}</DialogTitle>
        <DialogContent>
          <PlanPicker plans={plans.data ?? []} currentCode={trialOrExpired ? null : s.plan.code} busy={checkout.isPending || change.isPending}
            ctaLabel={trialOrExpired ? "Start subscription" : "Switch"}
            onChoose={(plan, interval, addons) => {
              if (trialOrExpired) checkout.mutate({ plan: plan.code, interval, addons }, { onSuccess: (d) => window.location.assign(d.url) });
              else change.mutate({ plan: plan.code, interval, addons }, { onSuccess: () => setChoosing(false) });
            }} />
        </DialogContent>
      </Dialog>
      <Dialog open={cancelling} onClose={() => setCancelling(false)}>
        <DialogTitle>We're sorry to see you go</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField label="What could we have done better?" multiline minRows={3} value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mt: 1 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={() => setCancelling(false)}>Keep subscription</Button>
            <Button color="error" variant="contained" disabled={cancel.isPending} onClick={() => cancel.mutate({ reason }, { onSuccess: () => setCancelling(false) })}>Cancel at period end</Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
